import type { Role, Action } from '~/types/rbac';
import { rbacConfig } from '~/config/rbacConfig';

import { authAtom } from '~/atoms/authAtom';
import { jotaiStore } from '~/atoms/authAtom';

/**
 * Check if a user with the given role can perform a specific action on a resource
 * @param resource - The resource to check access for
 * @param action - The action to check permission for
 * @returns boolean - Whether the user can perform the action on the resource
 */
export const canPerformAction = (resource: string, action: Action): boolean => {
  const auth = jotaiStore.get(authAtom);

  // If auth isn't loaded yet, return true to allow route guards to pass
  // The parent layout (_authLayout) will handle auth redirects when it renders
  if (!auth?.auth?.isLoaded) {
    return true;
  }

  const userRole = auth?.auth?.orgRole?.split(':')[1] as Role;
  const rolePermissions = rbacConfig.roles.find((r) => r.role === userRole);
  if (!rolePermissions) {
    return false;
  }
  const resourcePermission = rolePermissions.resources.find(
    (r) => r.resource === resource,
  );

  if (!resourcePermission) {
    return false;
  }

  return resourcePermission.actions.includes(action);
};

/**
 * Check if a user with the given role can access a specific resource
 * This maintains backward compatibility - it checks for 'read' permission
 * @param resource - The resource to check access for
 * @returns boolean - Whether the user has access to the resource
 */
export const canAccess = (resource: string): boolean => {
  return canPerformAction(resource, 'read');
};

/**
 * Get all accessible resources for a user with the given role
 * @returns string[] - Array of accessible resource names
 */
export const getAccessibleResources = (): string[] => {
  const auth = jotaiStore.get(authAtom);
  const userRole = auth?.auth?.orgRole?.split(':')[1] as Role;
  const rolePermissions = rbacConfig.roles.find((r) => r.role === userRole);

  return rolePermissions
    ? rolePermissions.resources.map((r) => r.resource)
    : [];
};

/**
 * Get all actions a user can perform on a specific resource
 * @param resource - The resource to check
 * @returns Action[] - Array of allowed actions
 */
export const getAllowedActions = (resource: string): Action[] => {
  const auth = jotaiStore.get(authAtom);
  const userRole = auth?.auth?.orgRole?.split(':')[1] as Role;
  const rolePermissions = rbacConfig.roles.find((r) => r.role === userRole);

  if (!rolePermissions) {
    return [];
  }

  const resourcePermission = rolePermissions.resources.find(
    (r) => r.resource === resource,
  );

  return resourcePermission ? resourcePermission.actions : [];
};

/**
 * Check if the current user has the portfolio_owner role
 * @returns boolean - Whether the user has portfolio_owner role
 */
export const isPortfolioOwner = (): boolean => {
  const auth = jotaiStore.get(authAtom);
  const userRole = auth?.auth?.orgRole?.split(':').pop() as Role;
  return userRole === 'portfolio_owner';
};
